<template>
  <div>
    <b-overlay :show="loading" spinner-variant="primary" rounded="md">

      <!-- Action Bar com filtros / ações -->
      <ActionBar
        :active-filters-count="activeFiltersCount"
        :filter-chips="filterChipsData"
        :is-loading="loading"
        :show-primary-action="false"
        @toggle-filters="toggleFilters"
        @remove-chip="handleRemoveChip"
      >
        <template #actions>
          <b-button
            size="sm"
            variant="primary"
            :disabled="loading"
            @click="pesquisar"
          >
            <feather-icon icon="SearchIcon" size="14" />
            <span class="ml-25">Pesquisar</span>
          </b-button>
          <b-button
            size="sm"
            variant="outline-secondary"
            @click="limparFiltros"
          >
            <feather-icon icon="XIcon" size="14" />
            <span class="ml-25">Limpar</span>
          </b-button>
          <b-button
            size="sm"
            variant="outline-primary"
            @click="gerarRelatorioPDF"
          >
            <feather-icon icon="FileTextIcon" size="14" />
            <span class="ml-25">Gerar PDF</span>
          </b-button>
        </template>
      </ActionBar>

      <!-- Filtros (colapsável) -->
      <b-collapse v-model="filtersOpen" id="filtros-comissoes-collapse" class="mb-1">
        <b-card no-body class="mb-1">
          <div class="m-2">
            <b-row>
              <b-col cols="12" md="4">
                <label>Representante</label>
                <b-form-input
                  v-model="filtros.cod_repres"
                  type="number"
                  placeholder="Código do representante"
                  :disabled="isRepresentanteLocked"
                  :readonly="isRepresentanteLocked"
                />
              </b-col>
              <b-col cols="12" md="4">
                <label>Mês</label>
                <b-form-select
                  v-model="filtroMesAno.mes"
                  :options="mesesOptions"
                  value-field="value"
                  text-field="text"
                />
              </b-col>
              <b-col cols="12" md="4">
                <label>Ano</label>
                <b-form-select
                  v-model="filtroMesAno.ano"
                  :options="anosOptions"
                />
              </b-col>
            </b-row>
            <b-row class="mt-1">
              <b-col cols="12" class="d-flex justify-content-end" style="gap: 0.5rem;">
                <b-button variant="outline-secondary" size="sm" @click="limparFiltrosDrawer">
                  <feather-icon icon="XIcon" size="14" />
                  <span class="ml-25">Limpar</span>
                </b-button>
                <b-button variant="primary" size="sm" @click="aplicarFiltros">
                  <feather-icon icon="CheckIcon" size="14" />
                  <span class="ml-25">Aplicar</span>
                </b-button>
              </b-col>
            </b-row>
          </div>
        </b-card>
      </b-collapse>

      <!-- KPIs -->
      <b-row v-if="dadosComissoes.length > 0" class="mb-1">
        <b-col cols="12" md="4">
          <b-card no-body class="mb-1">
            <div class="d-flex align-items-center p-1">
              <b-avatar size="40" variant="light-primary" class="mr-1">
                <feather-icon icon="HashIcon" size="18" />
              </b-avatar>
              <div>
                <h4 class="mb-0">{{ meta.total || 0 }}</h4>
                <small class="text-muted">Registros</small>
              </div>
            </div>
          </b-card>
        </b-col>
        <b-col cols="12" md="4">
          <b-card no-body class="mb-1">
            <div class="d-flex align-items-center p-1">
              <b-avatar size="40" variant="light-info" class="mr-1">
                <feather-icon icon="FileTextIcon" size="18" />
              </b-avatar>
              <div>
                <h4 class="mb-0">
                  <span v-if="!loadingTotals">{{ totalVlNota | formatCurrency }}</span>
                  <span v-else>...</span>
                </h4>
                <small class="text-muted">Total VL Nota</small>
              </div>
            </div>
          </b-card>
        </b-col>
        <b-col cols="12" md="4">
          <b-card no-body class="mb-1">
            <div class="d-flex align-items-center p-1">
              <b-avatar size="40" variant="light-success" class="mr-1">
                <feather-icon icon="DollarSignIcon" size="18" />
              </b-avatar>
              <div>
                <h4 class="mb-0">
                  <span v-if="!loadingTotals">{{ totalVlComissao | formatCurrency }}</span>
                  <span v-else>...</span>
                </h4>
                <small class="text-muted">Total Comissão</small>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Tabela Principal: Notas Fiscais -->
      <b-card no-body class="mb-1" v-if="dadosComissoes.length > 0">
        <div class="m-2">
          <b-row>
            <b-col
              cols="12"
              md="6"
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
            >
              <label>Mostrar</label>
              <b-form-select
                v-model="filtros.perPage"
                :options="pageOptions"
                class="per-page-selector d-inline-block mx-50"
                size="sm"
                @change="changePage"
              />
              <label>entradas</label>
            </b-col>
            <b-col cols="12" md="6" class="d-flex align-items-center justify-content-end">
              <h5 class="mb-0">Notas Fiscais</h5>
            </b-col>
          </b-row>
        </div>

        <b-table
          class="position-relative"
          :items="dadosComissoes"
          :fields="comissoesFields"
          responsive
          show-empty
          empty-text="Nenhum registro encontrado"
          primary-key="idx"
        >
          <template #cell(nr_doc)="data">
            {{ data.item.esp_docto }} {{ data.item.nr_nota_docto }}
          </template>
          <template #cell(dt_emis)="data">
            {{ data.item.dt_emis | formatDate }}
          </template>
          <template #cell(perc_comis)="data">
            {{ parseFloat(data.item.perc_comis || 0).toFixed(2) }}%
          </template>
          <template #cell(vl_nota)="data">
            {{ data.item.vl_nota | formatCurrency }}
          </template>
          <template #cell(vl_comis)="data">
            {{ data.item.vl_comis | formatCurrency }}
          </template>
        </b-table>

        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted">
                Mostrando {{ ((filtros.page - 1) * filtros.perPage) + 1 }} até
                {{ Math.min(filtros.page * filtros.perPage, meta.total || 0) }} de
                {{ meta.total || 0 }} registros
              </span>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <b-pagination
                v-model="filtros.page"
                :total-rows="meta.total || 0"
                :per-page="filtros.perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
                @change="onPageChange"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-card>

      <b-card v-else-if="!loading" class="text-center">
        <feather-icon icon="AlertTriangleIcon" size="48" class="text-muted mb-1" />
        <h5>Nenhum resultado encontrado</h5>
        <p class="text-muted mb-0">Ajuste os filtros e clique em Pesquisar</p>
      </b-card>

      <!-- Resumo por Marca -->
      <b-card no-body class="mb-1" v-if="dadosResumoCalculado.length > 0">
        <div class="m-2">
          <h5 class="mb-0">Resumo por Marca</h5>
        </div>
        <b-table
          class="position-relative"
          :items="dadosResumoCalculado"
          :fields="resumoFields"
          responsive
          show-empty
          empty-text="Nenhum registro encontrado"
        >
          <template #cell(vl_base_comis)="data">
            {{ data.item.vl_base_comis | formatCurrency }}
          </template>
          <template #cell(vl_acerto_debito)="data">
            {{ data.item.vl_acerto_debito | formatCurrency }}
          </template>
          <template #cell(vl_acerto_credito)="data">
            {{ data.item.vl_acerto_credito | formatCurrency }}
          </template>
          <template #cell(vl_total_comis)="data">
            <strong>{{ data.item.vl_total_comis | formatCurrency }}</strong>
          </template>
        </b-table>
      </b-card>

      <!-- Notas Fiscais por Tipo -->
      <b-card no-body class="mb-1" v-if="dadosNotasFiscaisCalculado.length > 0">
        <div class="m-2">
          <h5 class="mb-0">Notas Fiscais por Tipo</h5>
        </div>
        <b-table
          class="position-relative"
          :items="dadosNotasFiscaisCalculado"
          :fields="nfFields"
          responsive
          show-empty
          empty-text="Nenhum registro encontrado"
        >
          <template #cell(vl_comis_nf)="data">
            {{ data.item.vl_comis_nf | formatCurrency }}
          </template>
          <template #cell(vl_ir_comis)="data">
            {{ data.item.vl_ir_comis | formatCurrency }}
          </template>
        </b-table>
      </b-card>

    </b-overlay>
  </div>
</template>

<script>
import {
  BOverlay,
  BButton,
  BCard,
  BRow,
  BCol,
  BTable,
  BPagination,
  BFormInput,
  BFormSelect,
  BCollapse,
  BAvatar,
} from 'bootstrap-vue';
import moment from 'moment-timezone';
import { mapGetters, mapActions } from 'vuex';
import ComissoesService from '@/services/relatorios/comissoes.service';
import SLSRepresentanteService from '@/services/Cadastros/ApoioVendas/SLSRepresentante.service';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';
import ActionBar from '@/components/ActionBar.vue';

moment.tz.setDefault('America/Sao_Paulo');

const comissoesService = new ComissoesService();
const slsRepresentanteService = new SLSRepresentanteService();

export default {
  name: 'RelatorioComissoes',
  components: {
    BOverlay,
    BButton,
    BCard,
    BRow,
    BCol,
    BTable,
    BPagination,
    BFormInput,
    BFormSelect,
    BCollapse,
    BAvatar,
    ActionBar,
  },
  filters: {
    formatCurrency(value) {
      if (!value) return 'R$ 0,00';
      const numValue = parseFloat(value);
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(numValue);
    },
    formatDate(value) {
      if (!value) return '';
      return moment.utc(value).format('DD/MM/YYYY');
    },
  },
  data() {
    return {
      filtersOpen: false,
      isRepresentante: false,
      representantesCount: 0,
      isPaginating: false,
      pageOptions: [10, 20, 50, 100],
      filtros: {
        page: 1,
        perPage: 10,
        cod_repres: null,
        dt_emis: null,
      },
      filtroMesAno: {
        mes: moment().month() + 1,
        ano: moment().year(),
      },
      mesesOptions: [
        { value: 1, text: 'Janeiro' },
        { value: 2, text: 'Fevereiro' },
        { value: 3, text: 'Março' },
        { value: 4, text: 'Abril' },
        { value: 5, text: 'Maio' },
        { value: 6, text: 'Junho' },
        { value: 7, text: 'Julho' },
        { value: 8, text: 'Agosto' },
        { value: 9, text: 'Setembro' },
        { value: 10, text: 'Outubro' },
        { value: 11, text: 'Novembro' },
        { value: 12, text: 'Dezembro' },
      ],
      anosOptions: [],
      resumoList: [],
      nfEmissaoList: [],
      comissoesFields: [
        { key: 'cod_estab', label: 'Estab' },
        { key: 'nr_doc', label: 'NR Documento' },
        { key: 'dt_emis', label: 'Data' },
        { key: 'clienteiderp', label: 'Emitente' },
        { key: 'clienterazaosocial', label: 'Razão Social' },
        { key: 'des_marca', label: 'Marca' },
        { key: 'nro_ped_venda', label: 'Pedido' },
        { key: 'perc_comis', label: 'Porcentagem', tdClass: 'text-right', thClass: 'text-right' },
        { key: 'vl_nota', label: 'VL Base', tdClass: 'text-right', thClass: 'text-right' },
        { key: 'vl_comis', label: 'VL Comissão', tdClass: 'text-right', thClass: 'text-right' },
      ],
      resumoFields: [
        { key: 'des_marca', label: 'Marca' },
        { key: 'vl_base_comis', label: 'VL Comissão', tdClass: 'text-right', thClass: 'text-right' },
        { key: 'vl_acerto_debito', label: 'VL Débito', tdClass: 'text-right', thClass: 'text-right' },
        { key: 'vl_acerto_credito', label: 'VL Crédito', tdClass: 'text-right', thClass: 'text-right' },
        { key: 'vl_total_comis', label: 'VL Final', tdClass: 'text-right', thClass: 'text-right' },
      ],
      nfFields: [
        { key: 'nota_comis', label: 'Tipo de Nota' },
        { key: 'vl_comis_nf', label: 'Valor Comissão', tdClass: 'text-right', thClass: 'text-right' },
        { key: 'vl_ir_comis', label: 'Valor IRRF', tdClass: 'text-right', thClass: 'text-right' },
      ],
    };
  },
  computed: {
    ...mapGetters('comissoesStore', {
      dadosComissoes: 'getDadosComissoes',
      loading: 'getLoading',
      meta: 'getMeta',
      allDadosComissoes: 'getAllDadosComissoes',
      loadingTotals: 'getLoadingTotals',
    }),
    representanteIdInfoPedido() {
      try {
        return this.$store.getters['InfoPedido/GET_FORM_INFO_PEDIDO']?.representante?.representanteid || null;
      } catch (e) {
        return null;
      }
    },
    totalVlNota() {
      return this.dadosComissoes.reduce((sum, item) => sum + parseFloat(item.vl_nota || 0), 0);
    },
    totalVlComissao() {
      return this.resumoList.reduce((sum, item) => sum + parseFloat(item.vl_total_comis || 0), 0);
    },
    activeFiltersCount() {
      let count = 0;
      if (this.filtros.cod_repres && !this.isRepresentanteLocked) count++;
      return count;
    },
    hasActiveFilters() {
      return this.filtros.cod_repres || this.filtroMesAno.mes || this.filtroMesAno.ano;
    },
    filterChipsData() {
      const chips = [];
      if (this.filtros.cod_repres && !this.isRepresentanteLocked) {
        chips.push({ key: 'representante', label: `Rep: ${this.filtros.cod_repres}`, removable: true });
      }
      if (this.filtroMesAno.mes && this.filtroMesAno.ano) {
        chips.push({ key: 'mesano', label: `${this.getMesNome(this.filtroMesAno.mes)}/${this.filtroMesAno.ano}`, removable: false });
      }
      return chips;
    },
    dadosResumoCalculado() {
      return this.resumoList;
    },
    dadosNotasFiscaisCalculado() {
      return this.nfEmissaoList;
    },
    isRepresentanteLocked() {
      return this.isRepresentante && this.representantesCount < 2;
    },
  },
  methods: {
    ...mapActions('comissoesStore', [
      'setFilters',
      'clearFilters',
      'setLoading',
      'setAllDadosComissoes',
      'clearAllDadosComissoes',
      'setLoadingTotals',
    ]),
    toggleFilters() {
      this.filtersOpen = !this.filtersOpen;
    },
    handleRemoveChip(chip) {
      if (chip.key === 'representante') {
        this.clearRepresentante();
      }
    },
    aplicarFiltros() {
      this.filtersOpen = false;
      this.pesquisar();
    },
    limparFiltrosDrawer() {
      const codRepresFixo = this.isRepresentanteLocked ? this.filtros.cod_repres : null;
      this.filtros.cod_repres = codRepresFixo;
      this.filtroMesAno = { mes: moment().month() + 1, ano: moment().year() };
    },
    clearRepresentante() {
      if (!this.isRepresentanteLocked) {
        this.filtros.cod_repres = null;
      }
    },
    getMesNome(mes) {
      const m = this.mesesOptions.find((o) => o.value === mes);
      return m ? m.text.substring(0, 3) : '';
    },
    onPageChange(page) {
      this.filtros.page = page;
      this.changePage();
    },
    async buscarPaginaAtual() {
      try {
        this.isPaginating = true;
        this.setLoading(true);
        const dataCompleta = moment()
          .year(this.filtroMesAno.ano)
          .month(this.filtroMesAno.mes - 1)
          .date(1)
          .format('YYYY-MM-DD');
        const filtrosLimpos = { ...this.filtros, dt_emis: dataCompleta };
        Object.keys(filtrosLimpos).forEach((key) => { if (filtrosLimpos[key] === null || filtrosLimpos[key] === '') delete filtrosLimpos[key]; });
        const response = await comissoesService.findAll(filtrosLimpos);
        this.$store.commit('comissoesStore/SET_DADOS_COMISSOES', response.data);
        this.resumoList = response.resumo || [];
        this.nfEmissaoList = response.nf_emissao || [];
        this.$store.commit('comissoesStore/SET_META', {
          page: response.page,
          perPage: response.perPage,
          pages: response.pages,
          total: response.total,
        });
      } catch (error) {
        console.error('Erro ao buscar página:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro', text: 'Falha ao carregar a página.', icon: 'AlertCircleIcon', variant: 'danger',
          },
        });
      } finally {
        this.setLoading(false);
        this.isPaginating = false;
      }
    },
    async pesquisar() {
      try {
        this.setLoading(true);
        if (this.isRepresentanteLocked) {
          const repId = this.representanteIdInfoPedido;
          if (repId) this.filtros.cod_repres = repId;
        }
        this.filtros.page = 1;
        const dataCompleta = moment()
          .year(this.filtroMesAno.ano)
          .month(this.filtroMesAno.mes - 1)
          .date(1)
          .format('YYYY-MM-DD');
        const filtrosParaEnvio = { ...this.filtros, dt_emis: dataCompleta };
        const filtrosLimpos = { ...filtrosParaEnvio };
        Object.keys(filtrosLimpos).forEach((key) => { if (filtrosLimpos[key] === null || filtrosLimpos[key] === '') delete filtrosLimpos[key]; });
        const response = await comissoesService.findAll(filtrosLimpos);
        this.$store.commit('comissoesStore/SET_DADOS_COMISSOES', response.data);
        this.resumoList = response.resumo || [];
        this.nfEmissaoList = response.nf_emissao || [];
        const metaResp = {
          page: response.page,
          perPage: response.perPage,
          pages: response.pages,
          total: response.total,
        };
        this.$store.commit('comissoesStore/SET_META', metaResp);
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro', text: 'Erro ao buscar dados do relatório. Tente novamente.', icon: 'AlertCircleIcon', variant: 'danger',
          },
        });
      } finally {
        this.setLoading(false);
      }
    },
    limparFiltros() {
      const codRepresFixo = this.isRepresentanteLocked ? this.filtros.cod_repres : null;
      this.filtros = {
        page: 1, perPage: 10, cod_repres: codRepresFixo, dt_emis: null,
      };
      this.filtroMesAno = { mes: moment().month() + 1, ano: moment().year() };
      this.resumoList = [];
      this.nfEmissaoList = [];
      this.clearFilters();
      this.clearAllDadosComissoes();
    },
    async changePage() {
      await this.buscarPaginaAtual();
    },
    async gerarRelatorioPDF() {
      this.$toast({
        component: ToastificationContent,
        position: 'top-right',
        props: {
          title: 'Gerando PDF',
          text: 'Aguarde...',
          icon: 'LoaderIcon',
          variant: 'info',
        },
      });
      try {
        this.setLoading(true);
        if (this.isRepresentanteLocked) {
          const repId = this.representanteIdInfoPedido;
          if (repId) this.filtros.cod_repres = repId;
        }
        const dataCompleta = moment()
          .year(this.filtroMesAno.ano)
          .month(this.filtroMesAno.mes - 1)
          .date(1)
          .format('YYYY-MM-DD');
        const filtrosParaPdf = {
          cod_repres: this.filtros.cod_repres,
          dt_emis: dataCompleta,
          page: this.filtros.page,
          perPage: this.filtros.perPage,
        };
        const filtrosLimpos = { ...filtrosParaPdf };
        Object.keys(filtrosLimpos).forEach((key) => { if (filtrosLimpos[key] === null || filtrosLimpos[key] === '') delete filtrosLimpos[key]; });
        const response = await comissoesService.gerarPdfComissoes(filtrosLimpos);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const dataAtual = moment().format('DD-MM-YYYY_HH-mm-ss');
        const nomeArquivo = `relatorio-comissoes_${this.filtroMesAno.mes}-${this.filtroMesAno.ano}_${dataAtual}.pdf`;
        link.download = nomeArquivo;
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'PDF Gerado',
            text: 'Relatório PDF gerado e baixado com sucesso!',
            icon: 'FileTextIcon',
            variant: 'success',
          },
        });
      } catch (error) {
        console.error('Erro ao gerar PDF:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            text: 'Erro ao gerar o relatório PDF. Tente novamente.',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.setLoading(false);
      }
    },
    verificarPerfilUsuario() {
      const perfilLogado = JSON.parse(localStorage.getItem('perfilLogado'));
      const isRepresentante = perfilLogado && perfilLogado.perfil_acesso_nome === 'Representante';
      if (isRepresentante) {
        this.isRepresentante = true;
        const repIdInfo = this.representanteIdInfoPedido;
        if (repIdInfo) {
          this.filtros.cod_repres = repIdInfo;
        } else {
          const codRepresPerfil = perfilLogado ? (perfilLogado.cod_repres || perfilLogado.representanteid) : null;
          this.filtros.cod_repres = codRepresPerfil;
        }
      } else {
        this.isRepresentante = false;
      }
    },
    async contarRepresentantes() {
      if (!this.isRepresentante) {
        this.representantesCount = 0;
        return;
      }

      try {
        const representantes = await slsRepresentanteService.listarRepresentanteInfoPedido();
        this.representantesCount = Array.isArray(representantes) ? representantes.length : 0;
      } catch (error) {
        console.error('Erro ao contar representantes:', error);
        this.representantesCount = 0;
      }
    },
    gerarAnosOptions() {
      const anoAtual = moment().year();
      const anosDisponiveis = [];
      for (let i = anoAtual - 5; i <= anoAtual + 1; i++) {
        anosDisponiveis.push(i);
      }
      this.anosOptions = anosDisponiveis;
    },
  },
  created() {
    this.gerarAnosOptions();
    this.verificarPerfilUsuario();
    this.contarRepresentantes();
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>
